/**
 * GitHub OAuth Button Component for Backoffice
 * @implements FS-001.2.1 - GitHub OAuth Authentication
 *
 * A button that initiates GitHub OAuth authentication for backoffice login.
 * Redirects to the backend OAuth endpoint which handles state cookie generation
 * and redirects to GitHub's authorization page.
 */

import { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';

/**
 * Props for the GitHubOAuthButton component
 */
export interface GitHubOAuthButtonProps {
  /** Whether the button should be disabled */
  disabled?: boolean;
}

/**
 * GitHub OAuth button for backoffice login.
 *
 * On click, it redirects to `/auth/github?flow=login` which is handled by the
 * backend. The backend generates a state cookie and redirects to GitHub.
 *
 * @implements FS-001.2.1 - GitHub OAuth Authentication
 */
export const GitHubOAuthButton = ({ disabled }: GitHubOAuthButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle button click - initiate OAuth flow
   * @implements FS-001.2.1 - GitHub OAuth redirect to backend
   */
  const handleClick = () => {
    setIsLoading(true);
    // Redirect to backend OAuth endpoint with login flow
    window.location.href = '/auth/github?flow=login';
  };

  return (
    <Button
      variant="contained"
      onClick={handleClick}
      disabled={disabled || isLoading}
      startIcon={
        isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <GitHubIcon />
        )
      }
      fullWidth
      size="large"
      sx={{
        // GitHub branding colors
        backgroundColor: '#24292e',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#1b1f23',
        },
        '&:disabled': {
          backgroundColor: '#6a737d',
          color: '#ffffff',
        },
        textTransform: 'none',
        fontWeight: 500,
      }}
    >
      {isLoading ? 'Redirecting...' : 'Sign in with GitHub'}
    </Button>
  );
};
